import { Badge } from '@/components/ui/shadcn/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

interface InlineAccountingCellProps {
    value: boolean;
    onUpdate?: (value: boolean) => Promise<void>;
    className?: string;
    readOnly?: boolean;
}

export function InlineAccountingCell({ value, onUpdate, className, readOnly = false }: InlineAccountingCellProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSelect = async (newValue: boolean) => {
        if (newValue === value || !onUpdate) {
            setIsOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await onUpdate(newValue);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to update accounting:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const options = [
        { value: true, label: t('common.yes', 'Yes') },
        { value: false, label: t('common.no', 'No') },
    ];

    // Read-only mode: just display the badge
    if (readOnly || !onUpdate) {
        return (
            <div className={cn('flex w-full items-center justify-start px-2 py-1.5', className)}>
                <Badge variant="outline">{value ? t('common.yes', 'Yes') : t('common.no', 'No')}</Badge>
            </div>
        );
    }

    if (isMobile) {
        return (
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogTrigger asChild>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(true);
                        }}
                        className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                        ) : (
                            <Badge variant="outline">{value ? t('common.yes', 'Yes') : t('common.no', 'No')}</Badge>
                        )}
                    </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>{t('people.index.columns.accounting', 'Accounting')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-2">
                        {options.map((option) => (
                            <button
                                key={String(option.value)}
                                type="button"
                                onClick={() => handleSelect(option.value)}
                                className="bg-background hover:bg-accent flex w-full items-center justify-start rounded-md border px-3 py-2 text-left"
                            >
                                <Badge variant="outline">{option.label}</Badge>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(true);
                    }}
                    className={cn('hover:bg-accent w-full rounded px-2 py-1.5 transition-colors', 'flex items-center justify-start', className)}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                    ) : (
                        <Badge variant="outline">{value ? t('common.yes', 'Yes') : t('common.no', 'No')}</Badge>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
                {options.map((option) => (
                    <DropdownMenuItem key={String(option.value)} onClick={() => handleSelect(option.value)} className="cursor-pointer">
                        <Badge variant="outline">{option.label}</Badge>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
